import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface Collaborator {
  id: number;
  shared_with_email: string;
  shared_with_name?: string;
  role: string;
  status: string;
  created_at: string;
}

interface ShareModalProps {
  courseId: string;
  courseTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ courseId, courseTitle, isOpen, onClose }) => {
  const { token, user } = useAuth();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer'>('viewer');
  const [sharing, setSharing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchCollaborators();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchCollaborators = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/courses/${courseId}/collaborators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollaborators(response.data.collaborators);
    } catch (error: any) {
      console.error('Error fetching collaborators:', error);
      // Don't show error toast for 403, just means no access yet
      if (error.response?.status !== 403) {
        toast.error('Failed to load collaborators');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    setSharing(true);

    try {
      const response = await axios.post(
        `${API_URL}/courses/${courseId}/share`,
        { email, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || 'Course shared successfully!');
      setEmail('');
      setRole('viewer');
      await fetchCollaborators();
    } catch (error: any) {
      console.error('Error sharing course:', error);
      if (error.response?.data?.requiresUpgrade) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.response?.data?.error || 'Failed to share course');
      }
    } finally {
      setSharing(false);
    }
  };

  const handleUpdateRole = async (collaboratorEmail: string, newRole: string) => {
    try {
      await axios.put(
        `${API_URL}/courses/${courseId}/collaborators/${encodeURIComponent(collaboratorEmail)}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Role updated successfully');
      await fetchCollaborators();
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleRemoveCollaborator = async (collaboratorEmail: string) => {
    if (!window.confirm(`Remove ${collaboratorEmail} from this course?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/courses/${courseId}/collaborators/${encodeURIComponent(collaboratorEmail)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Collaborator removed successfully');
      await fetchCollaborators();
    } catch (error: any) {
      console.error('Error removing collaborator:', error);
      toast.error(error.response?.data?.error || 'Failed to remove collaborator');
    }
  };

  if (!isOpen) return null;

  const isBasic = user?.tier === 'basic';
  const isPremium = user?.tier === 'premium';
  const isFree = user?.tier === 'free';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Share Course</h2>
              <p className="text-purple-100 mt-1">{courseTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Free Tier Upgrade Prompt */}
          {isFree && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 text-center mb-6">
              <div className="text-5xl mb-3">🔒</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Team Collaboration is a Premium Feature
              </h3>
              <p className="text-gray-700 mb-4">
                Upgrade to Basic ($29/mo) to share courses with 2 collaborators, or Premium ($47/mo) for 5 team members!
              </p>
              <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition">
                Upgrade Now
              </button>
            </div>
          )}

          {/* Share Form */}
          {!isFree && (
            <>
              {isBasic && collaborators.length >= 2 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-700">
                    <strong>Basic Plan Limit:</strong> You can have 2 collaborators. <button className="text-purple-600 font-semibold hover:underline">Upgrade to Premium</button> for 5 team members!
                  </p>
                </div>
              )}

              <form onSubmit={handleShare} className="mb-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="colleague@example.com"
                      required
                      disabled={isBasic && collaborators.length >= 2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value as 'editor' | 'viewer')}
                      disabled={isBasic && collaborators.length >= 2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="viewer">Viewer - Can only view the course</option>
                      <option value="editor">Editor - Can edit the course</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={sharing || (isBasic && collaborators.length >= 2)}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sharing ? 'Sharing...' : 'Share Course'}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Collaborators List */}
          {!isFree && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Current Collaborators ({collaborators.length})
              </h3>

              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : collaborators.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <div className="text-4xl mb-2">👥</div>
                  <p className="text-gray-600">No collaborators yet</p>
                  <p className="text-sm text-gray-500 mt-1">Share this course to start collaborating!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {collaborators.map(collaborator => (
                    <div
                      key={collaborator.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                          {collaborator.shared_with_email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {collaborator.shared_with_name || collaborator.shared_with_email}
                          </p>
                          <p className="text-sm text-gray-600">{collaborator.shared_with_email}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <select
                          value={collaborator.role}
                          onChange={(e) => handleUpdateRole(collaborator.shared_with_email, e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                        </select>

                        <button
                          onClick={() => handleRemoveCollaborator(collaborator.shared_with_email)}
                          className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition"
                          title="Remove collaborator"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tier Info */}
          {!isFree && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {isBasic && 'Basic Plan: 2 collaborators per course'}
                  {isPremium && 'Premium Plan: 5 team collaborators'}
                </span>
                {isBasic && (
                  <button className="text-purple-600 font-semibold hover:underline">
                    Upgrade to Premium
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
